import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Upload, Plus, Pencil, Trash2, Package, DollarSign, Boxes, ShoppingBag, Image as ImageIcon, X, FileSpreadsheet, ScanBarcode, Truck, FileDown, RefreshCw, Store, Bell, Copy, Radio, Users, Mail, ArrowLeft, Eye, Phone, MapPin, Clock, Layers, Video, Check, Sparkles, Search, Printer } from "lucide-react";
import api, { money, assetUrl, formatApiErrorDetail } from "@/lib/api";
import { openPackingSlip } from "@/lib/packingSlip";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConditionBadge } from "@/components/ConditionBadge";
import { toast } from "sonner";

const EMPTY = { sku: "", inv_number: "", upc: "", brand: "", name: "", description: "", details: "", specs_status: "", condition: "Good / Fair Condition / Tested & Working", condition_notes: "", category: "General", lot_name: "", list_price: 0, our_price: 0, cost: 0, quantity: 1, weight_lbs: 1, images: [], videos: [] };

export default function Admin() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ conditions: [] });
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulk, setBulk] = useState({ condition: "", lot: "", discount: "" });
  const [notices, setNotices] = useState([]);
  const [reuploading, setReuploading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [lotCost, setLotCost] = useState({ lot: "", total: "" });
  const [lotMargins, setLotMargins] = useState(null);
  const [settings, setSettings] = useState({ min_margin_pct: 20, aging_days: 21, aging_sellthrough_pct: 50 });

  const loadAll = useCallback(async () => {
    const [m, s, p, o, n, lm, st] = await Promise.all([
      api.get("/meta"), api.get("/admin/stats"), api.get("/products", { params: { status: "all" } }), api.get("/admin/orders"), api.get("/admin/notices"), api.get("/admin/lot-margins"), api.get("/admin/settings"),
    ]);
    setMeta(m.data); setStats(s.data); setProducts(p.data); setOrders(o.data); setNotices(n.data); setLotMargins(lm.data); setSettings(st.data);
  }, []);

  useEffect(() => { if (user && user.role === "admin") loadAll().catch(() => {}); }, [user, loadAll]);

  if (user === null) return <div className="py-24 text-center font-mono text-subtle">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const openNew = () => { setEditing({ ...EMPTY }); setDialogOpen(true); };
  const openEdit = (p) => { setEditing({ ...p }); setDialogOpen(true); };
  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    loadAll();
  };

  const onShip = async (order) => {
    const tracking = window.prompt(`Enter the UPS Ground tracking number for ${order.customer?.name}'s order:`, "");
    if (tracking === null) return;
    if (!tracking.trim()) { toast.error("Tracking number required"); return; }
    try {
      await api.post(`/admin/orders/${order.id}/ship`, { tracking_number: tracking.trim(), carrier: "UPS Ground" });
      toast.success("Marked shipped — tracking email sent to buyer");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((prev) => prev.size === visibleProducts.length ? new Set() : new Set(visibleProducts.map((p) => p.id)));

  const pendingCount = products.filter((p) => p.status === "pending").length;
  const q = search.trim().toLowerCase();
  const visibleProducts = products.filter((p) =>
    (statusFilter === "all" ? true : statusFilter === "pending" ? p.status === "pending" : p.status !== "pending")
    && (!q || [p.name, p.sku, p.inv_number, p.upc, p.brand, p.category, p.lot_name, p.condition].some((f) => String(f || "").toLowerCase().includes(q)))
  );

  const publishIds = async (ids) => {
    const pendingIds = ids.filter((id) => products.find((p) => p.id === id)?.status === "pending");
    if (!pendingIds.length) { toast.error("Select held (pending) items to publish"); return; }
    try {
      const { data } = await api.post("/admin/products/publish", { product_ids: pendingIds });
      toast.success(`Published ${data.published} item${data.published === 1 ? "" : "s"} — now live`);
      setSelected(new Set()); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const floorCount = products.filter((p) => p.at_floor && !p.below_floor_approved).length;
  const approveMarkdown = async (ids, approved) => {
    if (!ids.length) { toast.error("Select items first"); return; }
    try {
      const { data } = await api.post("/admin/products/approve-markdown", { product_ids: ids, approved });
      toast.success(approved ? `Approved ${data.updated} item${data.updated === 1 ? "" : "s"} to discount below 20%` : "Below-floor approval revoked");
      setSelected(new Set()); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createLotBuy = async () => {
    const ids = [...selected];
    if (ids.length < 2) { toast.error("Select at least 2 floored items to group"); return; }
    const name = window.prompt("Name this Lot Buy:", `Lot Buy — ${ids.length} Items`);
    if (name === null) return;
    try {
      await api.post("/admin/lot-buys", { product_ids: ids, name });
      toast.success("Lot Buy created — see the Lot Buys tab");
      setSelected(new Set()); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const applyBulk = async () => {
    const payload = { product_ids: [...selected] };
    if (bulk.condition) payload.set_condition = bulk.condition;
    if (bulk.lot !== "") payload.set_lot_name = bulk.lot;
    if (bulk.discount !== "") payload.discount_pct = Number(bulk.discount);
    if (!payload.set_condition && payload.set_lot_name === undefined && payload.discount_pct === undefined) {
      toast.error("Choose at least one change"); return;
    }
    try {
      const { data } = await api.post("/admin/products/bulk", payload);
      toast.success(`Updated ${data.updated} products`);
      setSelected(new Set()); setBulk({ condition: "", lot: "", discount: "" }); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteBulk = async () => {
    if (!window.confirm(`Delete ${selected.size} selected products?`)) return;
    try {
      const { data } = await api.post("/admin/products/bulk", { product_ids: [...selected], delete: true });
      toast.success(`Deleted ${data.deleted} products`);
      setSelected(new Set()); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const bulkAutofillSpecs = async () => {
    const ids = [...selected];
    if (!ids.length) { toast.error("Select items first"); return; }
    const overwrite = window.confirm(`Auto-research specs for ${ids.length} product${ids.length === 1 ? "" : "s"}.\n\nOK = overwrite existing specs too.\nCancel = only fill products that have NO specs yet.`);
    try {
      const { data } = await api.post("/admin/products/bulk-autofill-specs", { product_ids: ids, overwrite });
      toast.success(`Researching specs for ${data.queued} products in the background — refresh in a moment to review the AI drafts.`);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const approveSpecs = async (ids) => {
    if (!ids.length) { toast.error("Select items first"); return; }
    try {
      const { data } = await api.post("/admin/products/approve-specs", { product_ids: ids });
      toast.success(data.updated ? `Verified specs on ${data.updated} product${data.updated === 1 ? "" : "s"} — now live` : "No AI-drafted specs in selection");
      setSelected(new Set()); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const ids = visibleProducts.map((p) => p.id);
      if (!ids.length) { toast.error("No items in the current view to export"); return; }
      const res = await api.post("/admin/products/export", { product_ids: ids }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      const tag = statusFilter === "pending" ? "held" : statusFilter === "published" ? "live" : "all";
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `str-surplus-${tag}-${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${ids.length} item${ids.length === 1 ? "" : "s"} from current view`);
    } catch (e) { toast.error("Export failed"); }
  };

  const reuploadCsv = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setReuploading(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/admin/products/bulk-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Updated ${data.updated} products${data.not_found ? ` · ${data.not_found} not matched` : ""}`);
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setReuploading(false); e.target.value = ""; }
  };

  const savePrice = async (id, val) => {
    const num = Number(val);
    if (isNaN(num) || num < 0) { toast.error("Enter a valid price"); return false; }
    try {
      await api.post(`/products/${id}/price`, { our_price: num });
      const prod = products.find((p) => p.id === id);
      const cost = prod?.cost || 0;
      if (cost > 0 && num > 0) {
        const marginPct = Math.round(((num - cost) / num) * 100);
        if (marginPct < settings.min_margin_pct) {
          toast.warning(`Margin ${marginPct}% is below your ${settings.min_margin_pct}% floor (cost ${money(cost)})`);
        } else {
          toast.success("Price updated");
        }
      } else {
        toast.success("Price updated");
      }
      setProducts((prev) => prev.map((p) => p.id === id
        ? { ...p, our_price: num, discount_pct: p.list_price > 0 ? Math.round((1 - num / p.list_price) * 100) : 0 }
        : p));
      return true;
    } catch (e) { toast.error("Update failed"); return false; }
  };

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Alert settings saved");
      loadAll();
    } catch (e) { toast.error("Could not save settings"); }
  };

  const markdownLot = async (lot) => {
    try {
      const { data } = await api.post(`/admin/lots/${encodeURIComponent(lot)}/markdown`, { pct: Number(settings.aging_markdown_pct) });
      toast.success(`Marked down ${data.items_marked_down} items in ${lot} by ${data.pct}%`);
      loadAll();
    } catch (e) { toast.error("Markdown failed"); }
  };

  const applyLotCost = async () => {
    if (!lotCost.lot) { toast.error("Choose a lot"); return; }
    if (lotCost.total === "" || isNaN(Number(lotCost.total))) { toast.error("Enter a total cost"); return; }
    try {
      const { data } = await api.post("/admin/lots/allocate-cost", { lot_name: lotCost.lot, total_cost: Number(lotCost.total) });
      toast.success(`Cost split across ${data.items_updated} items`);
      setLotCost({ lot: "", total: "" });
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const dismissNotice = async (id) => {
    try { await api.post(`/admin/notices/${id}/dismiss`); } catch (e) { /* noop */ }
    setNotices((prev) => prev.filter((x) => x.id !== id));
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8">
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mb-6">ADMIN CONSOLE</h1>

      {notices.map((n) => (
        <div key={n.id} data-testid={`notice-${n.id}`} className="mb-4 flex items-start gap-3 bg-discount border-2 border-ink p-4">
          <Bell className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-bold text-sm">{n.title}</div>
            <p className="font-mono text-xs text-ink/80 mt-1 leading-relaxed">{n.body}</p>
          </div>
          <button data-testid={`dismiss-notice-${n.id}`} onClick={() => dismissNotice(n.id)} className="p-1 hover:text-danger"><X className="h-4 w-4" /></button>
        </div>
      ))}

      {/* STATS */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
          <StatCard icon={Package} label="Products" value={stats.total_products} />
          <StatCard icon={Boxes} label="In Stock" value={stats.in_stock} />
          <StatCard icon={ShoppingBag} label="Paid Orders" value={stats.paid_orders} />
          <StatCard icon={DollarSign} label="Revenue" value={money(stats.revenue)} />
          <StatCard icon={DollarSign} label="Inv. Value" value={money(stats.inventory_value)} accent />
        </div>
      )}

      {lotMargins && lotMargins.lots.length > 0 && (
        <div data-testid="lot-margin-summary" className="border-2 border-ink mb-8">
          <div className="flex items-center gap-2 bg-ink text-paper px-4 py-2 font-mono text-xs uppercase tracking-wider">
            <DollarSign className="h-4 w-4" /> Lot Margin Summary — cost vs. sell value
          </div>

          {lotMargins.aging_count > 0 && (
            <div data-testid="aging-banner" className="flex items-center gap-2 bg-danger text-white px-4 py-2 font-mono text-[11px] uppercase tracking-wider">
              <Clock className="h-4 w-4" /> {lotMargins.aging_count} lot{lotMargins.aging_count > 1 ? "s" : ""} clearing slowly — consider discounting
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3 border-b border-ink/20 px-4 py-3 bg-surface">
            <span className="font-mono text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"><Bell className="h-3.5 w-3.5" /> Alert Settings</span>
            <label className="flex flex-col font-mono text-[10px] uppercase tracking-wider">Min margin %
              <input data-testid="set-min-margin" type="number" value={settings.min_margin_pct} onChange={(e) => setSettings({ ...settings, min_margin_pct: e.target.value })} className="mt-1 border border-ink h-8 px-2 bg-paper text-xs w-20" />
            </label>
            <label className="flex flex-col font-mono text-[10px] uppercase tracking-wider">Flag after (days)
              <input data-testid="set-aging-days" type="number" value={settings.aging_days} onChange={(e) => setSettings({ ...settings, aging_days: e.target.value })} className="mt-1 border border-ink h-8 px-2 bg-paper text-xs w-20" />
            </label>
            <label className="flex flex-col font-mono text-[10px] uppercase tracking-wider">Below sell-through %
              <input data-testid="set-aging-sellthrough" type="number" value={settings.aging_sellthrough_pct} onChange={(e) => setSettings({ ...settings, aging_sellthrough_pct: e.target.value })} className="mt-1 border border-ink h-8 px-2 bg-paper text-xs w-24" />
            </label>
            <label className="flex flex-col font-mono text-[10px] uppercase tracking-wider">Auto-markdown %
              <input data-testid="set-aging-markdown" type="number" value={settings.aging_markdown_pct} onChange={(e) => setSettings({ ...settings, aging_markdown_pct: e.target.value })} className="mt-1 border border-ink h-8 px-2 bg-paper text-xs w-24" />
            </label>
            <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider cursor-pointer">
              <input data-testid="set-aging-auto" type="checkbox" checked={Number(settings.aging_auto_markdown) === 1} onChange={(e) => setSettings({ ...settings, aging_auto_markdown: e.target.checked ? 1 : 0 })} className="h-4 w-4 accent-ink" />
              Auto-drop aging lots daily
            </label>
            <button data-testid="save-settings" onClick={saveSettings} className="bg-ink text-paper h-8 px-4 font-mono text-[10px] uppercase tracking-wider hover:bg-link transition-colors">Save</button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full font-mono text-xs">
              <thead className="text-subtle uppercase tracking-wider border-b border-ink">
                <tr>
                  <th className="text-left p-3">Lot / Pallet</th>
                  <th className="text-right p-3">In Stock</th>
                  <th className="text-right p-3">Sold</th>
                  <th className="text-right p-3">Sell-Through</th>
                  <th className="text-right p-3">Cost</th>
                  <th className="text-right p-3">Sell Value</th>
                  <th className="text-right p-3">Margin</th>
                  <th className="text-right p-3">Blended %</th>
                </tr>
              </thead>
              <tbody>
                {lotMargins.lots.map((l) => (
                  <tr key={l.lot_name} data-testid={`lot-margin-${l.lot_name}`} className={`border-b border-surface2 ${l.aging ? "bg-danger/10" : ""}`}>
                    <td className="p-3 font-bold">
                      {l.lot_name}
                      {l.aging && <span data-testid={`aging-flag-${l.lot_name}`} className="ml-2 inline-block bg-danger text-white text-[9px] px-1.5 py-0.5 tracking-wider">SLOW · {l.age_days}d</span>}
                      {l.aging && (
                        <button data-testid={`markdown-lot-${l.lot_name}`} onClick={() => markdownLot(l.lot_name)}
                          className="ml-2 inline-flex items-center gap-1 bg-discount text-ink text-[9px] px-1.5 py-0.5 tracking-wider border border-ink hover:bg-ink hover:text-paper transition-colors">
                          MARK DOWN {settings.aging_markdown_pct}%
                        </button>
                      )}
                    </td>
                    <td className="p-3 text-right">{l.units}</td>
                    <td className="p-3 text-right">{l.sold}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-2 bg-surface2 border border-ink/30 overflow-hidden">
                          <div className="h-full bg-success" style={{ width: `${l.sell_through_pct}%` }} />
                        </div>
                        <span className="w-8 text-right" data-testid={`sellthrough-${l.lot_name}`}>{l.sell_through_pct}%</span>
                      </div>
                    </td>
                    <td className="p-3 text-right">{l.cost ? money(l.cost) : <span className="text-subtle">— set cost</span>}</td>
                    <td className="p-3 text-right font-bold">{money(l.sell)}</td>
                    <td className="p-3 text-right" style={{ color: l.margin >= 0 ? "#16A34A" : "#FF3B30" }}>{money(l.margin)}</td>
                    <td className="p-3 text-right font-bold" style={{ color: l.margin >= 0 ? "#16A34A" : "#FF3B30" }}>{l.margin_pct}%</td>
                  </tr>
                ))}
                <tr className="bg-discount/40 border-t-2 border-ink font-bold">
                  <td className="p-3 uppercase">All Lots</td>
                  <td className="p-3 text-right">{lotMargins.total.units}</td>
                  <td className="p-3 text-right">{lotMargins.total.sold}</td>
                  <td className="p-3 text-right" data-testid="sellthrough-total">{lotMargins.total.sell_through_pct}%</td>
                  <td className="p-3 text-right">{money(lotMargins.total.cost)}</td>
                  <td className="p-3 text-right">{money(lotMargins.total.sell)}</td>
                  <td className="p-3 text-right">{money(lotMargins.total.margin)}</td>
                  <td className="p-3 text-right" data-testid="lot-margin-blended">{lotMargins.total.margin_pct}%</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="font-mono text-[10px] text-subtle px-4 py-2 border-t border-surface2">Cost is $0 until you set a lot cost below. Margin = Sell Value − Cost.</p>
        </div>
      )}

      <Tabs defaultValue="products">
        <TabsList className="bg-surface border border-ink rounded-none p-0 h-auto">
          <TabsTrigger value="products" data-testid="tab-products" className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-ink data-[state=active]:text-paper px-4 py-2">Products</TabsTrigger>
          <TabsTrigger value="manifest" data-testid="tab-manifest" className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-ink data-[state=active]:text-paper px-4 py-2">Upload Manifest</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-ink data-[state=active]:text-paper px-4 py-2">Orders</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-ink data-[state=active]:text-paper px-4 py-2">Customers</TabsTrigger>
          <TabsTrigger value="lotbuys" data-testid="tab-lotbuys" className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-ink data-[state=active]:text-paper px-4 py-2">Lot Buys</TabsTrigger>
          <TabsTrigger value="channels" data-testid="tab-channels" className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-ink data-[state=active]:text-paper px-4 py-2">Channels</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos" className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-ink data-[state=active]:text-paper px-4 py-2">Photo Match</TabsTrigger>
        </TabsList>

        {/* PRODUCTS */}
        <TabsContent value="products" className="mt-6">
          {pendingCount > 0 && (
            <div data-testid="pending-review-banner" className="flex flex-wrap items-center gap-3 bg-discount border-2 border-ink p-3 mb-4">
              <span className="font-mono text-xs uppercase tracking-wider font-bold">{pendingCount} item{pendingCount === 1 ? "" : "s"} held for review — not live on the store yet</span>
              <button data-testid="review-pending-btn" onClick={() => setStatusFilter("pending")} className="bg-ink text-paper h-8 px-4 font-mono text-[10px] uppercase tracking-wider">Review Held Items</button>
            </div>
          )}
          {floorCount > 0 && (
            <div data-testid="floor-review-banner" className="flex flex-wrap items-center gap-3 bg-danger text-white border-2 border-ink p-3 mb-4">
              <span className="font-mono text-xs uppercase tracking-wider font-bold">{floorCount} item{floorCount === 1 ? "" : "s"} hit the 20% retail floor — approve to discount further</span>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mb-3 font-mono text-[10px] uppercase tracking-wider">
            {[["all", "All"], ["live", "Live"], ["pending", "Held"]].map(([v, l]) => (
              <button key={v} data-testid={`filter-status-${v}`} onClick={() => setStatusFilter(v)} className={`px-3 h-8 border border-ink ${statusFilter === v ? "bg-ink text-paper" : "bg-paper hover:bg-surface2"}`}>{l}{v === "pending" && pendingCount > 0 ? ` (${pendingCount})` : ""}</button>
            ))}
            <div className="relative flex-1 min-w-[180px] max-w-sm ml-auto">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-subtle" />
              <input data-testid="admin-product-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, SKU, UPC, brand…" className="w-full border border-ink h-8 pl-7 pr-7 bg-paper text-xs normal-case focus:outline-none focus:ring-2 focus:ring-ink" />
              {search && <button data-testid="admin-product-search-clear" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-subtle hover:text-ink"><X className="h-3.5 w-3.5" /></button>}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <button data-testid="export-csv-button" onClick={exportCsv} className="flex items-center gap-2 border border-ink px-4 h-10 font-mono text-xs uppercase tracking-wider hover:bg-ink hover:text-paper transition-colors">
                <FileDown className="h-4 w-4" /> Export View ({visibleProducts.length})
              </button>
              <label data-testid="reupload-csv-label" className="flex items-center gap-2 border border-ink px-4 h-10 font-mono text-xs uppercase tracking-wider hover:bg-ink hover:text-paper transition-colors cursor-pointer">
                <RefreshCw className="h-4 w-4" /> {reuploading ? "Updating…" : "Re-Upload CSV"}
                <input data-testid="reupload-csv-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={reuploadCsv} />
              </label>
            </div>
            <button data-testid="add-product-button" onClick={openNew} className="flex items-center gap-2 bg-ink text-paper px-4 h-10 font-mono text-xs uppercase tracking-wider hover:bg-link transition-colors">
              <Plus className="h-4 w-4" /> Add Product
            </button>
          </div>

          <div data-testid="lot-cost-bar" className="flex flex-wrap items-end gap-3 border border-ink p-3 mb-4">
            <span className="font-mono text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> Set Lot Cost (splits across items by retail → shows margins)</span>
            <label className="flex flex-col font-mono text-[10px] uppercase tracking-wider">Lot / Pallet
              <select data-testid="lotcost-lot" value={lotCost.lot} onChange={(e) => setLotCost({ ...lotCost, lot: e.target.value })} className="mt-1 border border-ink h-8 px-1 bg-paper text-xs w-48">
                <option value="">— choose —</option>
                {(meta.lots || []).map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>
            <label className="flex flex-col font-mono text-[10px] uppercase tracking-wider">Total cost paid ($)
              <input data-testid="lotcost-total" type="number" value={lotCost.total} onChange={(e) => setLotCost({ ...lotCost, total: e.target.value })} placeholder="e.g. 1800" className="mt-1 border border-ink h-8 px-2 bg-paper text-xs w-28" />
            </label>
            <button data-testid="lotcost-apply" onClick={applyLotCost} className="bg-ink text-paper h-8 px-4 font-mono text-[10px] uppercase tracking-wider hover:bg-link transition-colors">Allocate</button>
          </div>

          {selected.size > 0 && (
            <div data-testid="bulk-bar" className="flex flex-wrap items-end gap-3 bg-discount border-2 border-ink p-3 mb-4">
              <span className="font-mono text-xs uppercase tracking-wider font-bold">{selected.size} selected</span>
              <label className="flex flex-col font-mono text-[10px] uppercase tracking-wider">Set condition
                <select data-testid="bulk-condition" value={bulk.condition} onChange={(e) => setBulk({ ...bulk, condition: e.target.value })} className="mt-1 border border-ink h-8 px-1 bg-paper text-xs">
                  <option value="">—</option>
                  {meta.conditions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="flex flex-col font-mono text-[10px] uppercase tracking-wider">Set lot
                <input data-testid="bulk-lot" value={bulk.lot} onChange={(e) => setBulk({ ...bulk, lot: e.target.value })} className="mt-1 border border-ink h-8 px-2 bg-paper text-xs w-32" />
              </label>
              <label className="flex flex-col font-mono text-[10px] uppercase tracking-wider">Discount %
                <input data-testid="bulk-discount" type="number" value={bulk.discount} onChange={(e) => setBulk({ ...bulk, discount: e.target.value })} placeholder="e.g. 40" className="mt-1 border border-ink h-8 px-2 bg-paper text-xs w-20" />
              </label>
              <button data-testid="bulk-apply" onClick={applyBulk} className="bg-ink text-paper h-8 px-4 font-mono text-[10px] uppercase tracking-wider">Apply</button>
              <button data-testid="bulk-publish" onClick={() => publishIds([...selected])} className="bg-success text-ink border border-ink h-8 px-4 font-mono text-[10px] uppercase tracking-wider hover:opacity-90">Publish (Go Live)</button>
              <button data-testid="bulk-approve-markdown" onClick={() => approveMarkdown([...selected], true)} className="border border-danger text-danger h-8 px-4 font-mono text-[10px] uppercase tracking-wider hover:bg-danger hover:text-white transition-colors">Approve &lt;20%</button>
              <button data-testid="bulk-lot-buy" onClick={createLotBuy} className="bg-link text-white border border-ink h-8 px-4 font-mono text-[10px] uppercase tracking-wider hover:opacity-90">Group into Lot Buy</button>
              <button data-testid="bulk-autofill-specs" onClick={bulkAutofillSpecs} className="bg-ink text-paper border border-ink h-8 px-4 font-mono text-[10px] uppercase tracking-wider hover:opacity-90 inline-flex items-center gap-1.5"><Sparkles className="h-3 w-3" />Auto-fill Specs</button>
              <button data-testid="bulk-approve-specs" onClick={() => approveSpecs([...selected])} className="bg-success text-ink border border-ink h-8 px-4 font-mono text-[10px] uppercase tracking-wider hover:opacity-90 inline-flex items-center gap-1.5"><Check className="h-3 w-3" />Verify Specs</button>
              <button data-testid="bulk-delete" onClick={deleteBulk} className="border border-danger text-danger h-8 px-4 font-mono text-[10px] uppercase tracking-wider hover:bg-danger hover:text-white transition-colors">Delete Selected</button>
              <button onClick={() => setSelected(new Set())} className="font-mono text-[10px] uppercase tracking-wider underline">Clear</button>
            </div>
          )}

          <div className="border border-ink overflow-x-auto">
            <table className="w-full font-mono text-xs">
              <thead className="bg-ink text-paper uppercase tracking-wider">
                <tr>
                  <th className="p-3"><input data-testid="select-all" type="checkbox" checked={visibleProducts.length > 0 && selected.size === visibleProducts.length} onChange={toggleAll} className="h-4 w-4 accent-discount" /></th>
                  <th className="text-left p-3">Product</th>
                  <th className="text-left p-3">SKU</th>
                  <th className="text-left p-3">Inv#</th>
                  <th className="text-left p-3">Condition</th>
                  <th className="text-right p-3">List</th>
                  <th className="text-right p-3">Our</th>
                  <th className="text-right p-3">% Retail</th>
                  <th className="text-right p-3">Cost</th>
                  <th className="text-right p-3">Margin</th>
                  <th className="text-right p-3">Qty</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleProducts.length === 0 && (
                  <tr><td colSpan="12" className="p-8 text-center text-subtle">No products in this view.</td></tr>
                )}
                {visibleProducts.map((p) => (
                  <tr key={p.id} data-testid={`admin-row-${p.id}`} className={`border-t border-surface2 hover:bg-surface ${selected.has(p.id) ? "bg-discount/20" : ""}`}>
                    <td className="p-3"><input data-testid={`select-${p.id}`} type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} className="h-4 w-4 accent-ink" /></td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 max-w-xs">
                        <img src={(p.images && p.images[0]) || ""} alt="" className="h-8 w-8 object-cover border border-ink shrink-0" />
                        <span className="truncate">{p.name}</span>
                        {p.status === "pending" && <span data-testid={`held-badge-${p.id}`} className="shrink-0 bg-discount text-ink border border-ink px-1.5 py-0.5 text-[9px] uppercase tracking-wider">Held</span>}
                        {p.at_floor && !p.below_floor_approved && <span data-testid={`floor-badge-${p.id}`} className="shrink-0 bg-danger text-white border border-ink px-1.5 py-0.5 text-[9px] uppercase tracking-wider">20% Floor</span>}
                        {p.below_floor_approved && <span data-testid={`belowfloor-ok-${p.id}`} className="shrink-0 bg-success text-ink border border-ink px-1.5 py-0.5 text-[9px] uppercase tracking-wider">&lt;20% OK</span>}
                        {p.bundled_in && <span data-testid={`bundled-badge-${p.id}`} className="shrink-0 bg-link text-white border border-ink px-1.5 py-0.5 text-[9px] uppercase tracking-wider">In Lot Buy</span>}
                        {p.specs_status === "ai_draft" && <span data-testid={`specs-draft-badge-${p.id}`} title="AI-drafted specs — hidden from buyers & feeds until verified" className="shrink-0 bg-discount text-ink border border-ink px-1.5 py-0.5 text-[9px] uppercase tracking-wider">AI-Drafted · Verify</span>}
                      </div>
                    </td>
                    <td className="p-3">{p.sku || "—"}</td>
                    <td className="p-3 font-mono text-xs" data-testid={`inv-${p.id}`}>{p.inv_number || "—"}</td>
                    <td className="p-3"><ConditionBadge condition={p.condition} /></td>
                    <td className="p-3 text-right">{money(p.list_price)}</td>
                    <td className="p-3 text-right"><EditableOurPrice p={p} onSave={savePrice} /></td>
                    <td className="p-3 text-right" data-testid={`pct-retail-${p.id}`}>{p.list_price > 0 ? Math.round((p.our_price / p.list_price) * 100) : 0}%</td>
                    <td className="p-3 text-right text-subtle">{p.cost ? money(p.cost) : "—"}</td>
                    <td className="p-3 text-right"><MarginCell p={p} minMargin={Number(settings.min_margin_pct)} /></td>
                    <td className="p-3 text-right">{p.quantity}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {p.status === "pending" && (
                        <button data-testid={`publish-${p.id}`} onClick={() => publishIds([p.id])} className="mr-2 border border-success text-success px-2 py-0.5 text-[10px] uppercase tracking-wider hover:bg-success hover:text-ink transition-colors">Publish</button>
                      )}
                      {p.at_floor && !p.below_floor_approved && (
                        <button data-testid={`approve-markdown-${p.id}`} onClick={() => approveMarkdown([p.id], true)} className="mr-2 border border-danger text-danger px-2 py-0.5 text-[10px] uppercase tracking-wider hover:bg-danger hover:text-white transition-colors">Approve &lt;20%</button>
                      )}
                      {p.below_floor_approved && (
                        <button data-testid={`revoke-markdown-${p.id}`} onClick={() => approveMarkdown([p.id], false)} className="mr-2 border border-ink text-subtle px-2 py-0.5 text-[10px] uppercase tracking-wider hover:bg-ink hover:text-paper transition-colors">Revoke</button>
                      )}
                      {p.specs_status === "ai_draft" && (
                        <button data-testid={`verify-specs-${p.id}`} onClick={() => approveSpecs([p.id])} className="mr-2 border border-success text-success px-2 py-0.5 text-[10px] uppercase tracking-wider hover:bg-success hover:text-ink transition-colors">Verify Specs</button>
                      )}
                      <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="p-1 hover:text-link"><Pencil className="h-4 w-4 inline" /></button>
                      <button data-testid={`delete-${p.id}`} onClick={() => del(p.id)} className="p-1 hover:text-danger ml-2"><Trash2 className="h-4 w-4 inline" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* MANIFEST */}
        <TabsContent value="manifest" className="mt-6">
          <ManifestUpload onDone={loadAll} />
        </TabsContent>

        {/* ORDERS */}
        <TabsContent value="orders" className="mt-6">
          <div className="border border-ink overflow-x-auto">
            <table className="w-full font-mono text-xs">
              <thead className="bg-ink text-paper uppercase tracking-wider">
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Fulfillment</th>
                  <th className="text-left p-3">Items</th>
                  <th className="text-right p-3">Total</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Fulfil</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-subtle">No orders yet.</td></tr>}
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-t border-surface2 hover:bg-surface">
                    <td className="p-3">{(o.created_at || "").slice(0, 10)}</td>
                    <td className="p-3">{o.customer?.name}<br /><span className="text-subtle">{o.customer?.email}</span></td>
                    <td className="p-3 uppercase">{o.fulfillment}</td>
                    <td className="p-3">
                      <div className="space-y-1">
                        {(o.items || []).map((li, idx) => (
                          <div key={idx} data-testid={`order-line-${o.id}-${idx}`} className="leading-tight">
                            <span className="font-bold">{li.quantity}×</span> {li.name}
                            <div className="text-subtle text-[10px]">
                              SKU {li.sku || "—"}
                              {li.inv_number ? <span className="text-ink font-bold"> · Inv# {li.inv_number}</span> : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="p-3 text-right font-bold">{money(o.total)}</td>
                    <td className="p-3"><span className={o.status === "paid" ? "bg-success text-ink px-2 py-1 uppercase" : o.status === "shipped" ? "bg-link text-white px-2 py-1 uppercase" : "bg-surface2 px-2 py-1 uppercase"}>{o.status}</span></td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`print-slip-${o.id}`} onClick={() => openPackingSlip(o)} className="inline-flex items-center gap-1 border border-ink px-2 py-1 uppercase hover:bg-ink hover:text-paper transition-colors">
                          <Printer className="h-3.5 w-3.5" /> Slip
                        </button>
                        {o.status === "paid" && o.fulfillment === "ship" && (
                          <button data-testid={`ship-${o.id}`} onClick={() => onShip(o)} className="inline-flex items-center gap-1 border border-ink px-2 py-1 uppercase hover:bg-ink hover:text-paper transition-colors">
                            <Truck className="h-3.5 w-3.5" /> Mark Shipped
                          </button>
                        )}
                        {o.status === "shipped" && <span className="text-subtle">#{o.tracking_number}</span>}
                        {o.status === "paid" && o.fulfillment === "pickup" && <span className="text-subtle">Pickup</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* CHANNELS */}
        <TabsContent value="customers" className="mt-6">
          <CustomersPanel />
        </TabsContent>

        <TabsContent value="lotbuys" className="mt-6">
          <LotBuysPanel />
        </TabsContent>

        <TabsContent value="channels" className="mt-6">
          <ChannelsPanel />
        </TabsContent>

        <TabsContent value="photos" className="mt-6">
          <PhotoMatchPanel products={products} onDone={loadAll} />
        </TabsContent>
      </Tabs>

      {dialogOpen && editing && (
        <ProductDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          product={editing}
          conditions={meta.conditions}
          onSaved={() => { setDialogOpen(false); loadAll(); }}
        />
      )}
    </div>
  );
}

function keyIndexFrom(products) {
  const m = new Map();
  const add = (k, p) => {
    if (!k) return;
    const key = String(k).trim().toLowerCase();
    if (!key) return;
    if (!m.has(key)) m.set(key, []);
    if (!m.get(key).some((x) => x.id === p.id)) m.get(key).push(p);
  };
  (products || []).forEach((p) => { if (p.is_lot_buy) return; add(p.sku, p); add(p.upc, p); });
  return m;
}

function deriveSku(filename, keyMap) {
  let base = filename.replace(/\.[^.]+$/, "").trim();
  if (keyMap.has(base.toLowerCase())) return base;
  const stripped = base.replace(/[\s_-]*(?:copy|\(\d+\)|\d+)\s*$/i, "").trim();
  if (stripped && keyMap.has(stripped.toLowerCase())) return stripped;
  return base;
}

function PhotoMatchPanel({ products, onDone }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const idx = keyIndexFrom(products);

  const gradesFor = (id) => idx.get(String(id).trim().toLowerCase()) || [];
  const matchRow = (id) => { const g = gradesFor(id); return g.length ? { name: g[0].name, count: g.length } : null; };
  const shortCond = (c) => String(c || "").split("/")[0].trim() || "Grade";

  const addFiles = (fileList) => {
    const files = [...fileList].filter((f) => f.type.startsWith("image/"));
    files.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    const seen = new Set();
    const next = files.map((file) => {
      const sku = deriveSku(file.name, idx);
      const grades = gradesFor(sku);
      const key = sku.toLowerCase();
      const hero = grades.length > 0 && !seen.has(key);
      if (grades.length > 0) seen.add(key);
      return { id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`, file, name: file.name, preview: URL.createObjectURL(file), sku, selectedIds: grades.map((g) => g.id), hero: !!hero, status: "pending" };
    });
    setRows((r) => [...r, ...next]);
  };

  const setRow = (id, patch) => setRows((r) => r.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const removeRow = (id) => setRows((r) => r.filter((x) => x.id !== id));
  const changeSku = (id, sku) => setRow(id, { sku, selectedIds: gradesFor(sku).map((g) => g.id) });
  const toggleGrade = (id, gid) => setRows((r) => r.map((x) => {
    if (x.id !== id) return x;
    const on = x.selectedIds.includes(gid);
    return { ...x, selectedIds: on ? x.selectedIds.filter((i) => i !== gid) : [...x.selectedIds, gid] };
  }));

  const isReady = (r) => gradesFor(r.sku).length > 0 && r.selectedIds.length > 0;
  const readyCount = rows.filter(isReady).length;

  const uploadAll = async () => {
    const todo = rows.filter((r) => isReady(r) && r.status !== "done");
    if (!todo.length) { toast.error("Match at least one file to a grade first"); return; }
    setBusy(true);
    let ok = 0, fail = 0;
    for (const r of todo) {
      setRow(r.id, { status: "uploading" });
      try {
        const fd = new FormData();
        fd.append("file", r.file);
        fd.append("sku", r.sku);
        fd.append("product_ids", r.selectedIds.join(","));
        fd.append("hero", r.hero ? "true" : "false");
        const { data } = await api.post("/admin/products/photo-match", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.matched > 0) { ok++; setRow(r.id, { status: "done" }); }
        else { fail++; setRow(r.id, { status: "nomatch" }); }
      } catch (e) { fail++; setRow(r.id, { status: "error" }); }
    }
    setBusy(false);
    toast.success(`Uploaded ${ok} photo${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed/unmatched` : ""}`);
    onDone && onDone();
  };

  return (
    <div className="space-y-4" data-testid="photo-match-panel">
      <div>
        <h3 className="font-heading font-black text-xl tracking-tight">Bulk Photo Match</h3>
        <p className="font-mono text-xs text-subtle mt-1 max-w-2xl">Drop photos straight from your Mac or phone. Name each file with its <strong>SKU or UPC/barcode</strong> (e.g. <code className="bg-surface px-1">VS2295.jpg</code> or <code className="bg-surface px-1">033413006079.jpg</code>). Extra shots of the same item: add a suffix like <code className="bg-surface px-1">VS2295-2.jpg</code>. If it has multiple condition grades, tick which grade(s) the photo belongs to. The first photo per item becomes the hero. Photos are hosted by us and show everywhere, including the Facebook/Whatnot feeds.</p>
      </div>

      <label data-testid="photo-drop" htmlFor="photo-input"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-ink p-10 cursor-pointer hover:bg-surface transition-colors text-center">
        <ImageIcon className="h-8 w-8" />
        <span className="font-mono text-sm uppercase tracking-wider">Drag & drop photos here, or click to choose</span>
        <span className="font-mono text-[11px] text-subtle">JPG · PNG · WEBP — many at once</span>
        <input id="photo-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </label>

      {rows.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-subtle">{rows.length} file{rows.length === 1 ? "" : "s"} · <span className="text-ink font-bold">{readyCount} ready</span>{rows.length - readyCount > 0 && <span className="text-danger"> · {rows.length - readyCount} need a grade</span>}</span>
            <div className="flex gap-2">
              <button data-testid="photo-clear" onClick={() => setRows([])} disabled={busy} className="border border-ink h-9 px-4 font-mono text-[11px] uppercase tracking-wider hover:bg-ink hover:text-paper transition-colors disabled:opacity-40">Clear</button>
              <button data-testid="photo-upload-all" onClick={uploadAll} disabled={busy || readyCount === 0} className="bg-ink text-paper h-9 px-5 font-mono text-[11px] uppercase tracking-wider hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2"><Upload className="h-3.5 w-3.5" />{busy ? "Uploading…" : `Upload & Assign ${readyCount}`}</button>
            </div>
          </div>

          <div className="border border-ink divide-y divide-surface2">
            {rows.map((r) => {
              const grades = gradesFor(r.sku);
              const m = matchRow(r.sku);
              return (
                <div key={r.id} data-testid={`photo-row-${r.id}`} className="flex items-start gap-3 p-3">
                  <img src={r.preview} alt="" className="h-14 w-14 object-cover border border-ink shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs truncate">{r.name}</span>
                      <input data-testid={`photo-sku-${r.id}`} value={r.sku} onChange={(e) => changeSku(r.id, e.target.value)} placeholder="SKU / UPC" className="border border-ink h-7 px-2 bg-paper text-xs w-32 font-mono shrink-0" />
                    </div>
                    {m ? <div className="font-mono text-[11px] text-success truncate mt-0.5">→ {m.name}</div>
                       : <div className="font-mono text-[11px] text-danger mt-0.5">No product with this SKU or UPC</div>}
                    {grades.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {grades.length > 1 && (
                          <span className="font-mono text-[9px] uppercase tracking-wider text-subtle mr-1">Assign to:</span>
                        )}
                        {grades.map((g) => {
                          const on = r.selectedIds.includes(g.id);
                          return (
                            <button key={g.id} type="button" data-testid={`photo-grade-${r.id}-${g.id}`} onClick={() => toggleGrade(r.id, g.id)}
                              className={`font-mono text-[10px] uppercase tracking-wider border px-2 py-0.5 transition-colors ${on ? "bg-ink text-paper border-ink" : "bg-paper text-subtle border-ink/40 hover:border-ink"}`}>
                              {on ? "✓ " : ""}{shortCond(g.condition)}
                            </button>
                          );
                        })}
                        {grades.length > 1 && (
                          <button type="button" onClick={() => setRow(r.id, { selectedIds: r.selectedIds.length === grades.length ? [] : grades.map((g) => g.id) })}
                            className="font-mono text-[10px] uppercase tracking-wider underline text-link ml-1">
                            {r.selectedIds.length === grades.length ? "None" : "All"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <label className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider cursor-pointer shrink-0 pt-1">
                    <input data-testid={`photo-hero-${r.id}`} type="checkbox" checked={r.hero} onChange={(e) => setRow(r.id, { hero: e.target.checked })} className="h-3.5 w-3.5 accent-ink" />Hero
                  </label>
                  <div className="w-6 text-center shrink-0 pt-1">
                    {r.status === "done" && <Check className="h-4 w-4 text-success inline" />}
                    {r.status === "uploading" && <RefreshCw className="h-4 w-4 animate-spin inline" />}
                    {(r.status === "error" || r.status === "nomatch") && <X className="h-4 w-4 text-danger inline" />}
                    {r.status === "pending" && <button onClick={() => removeRow(r.id)} className="text-subtle hover:text-danger"><X className="h-4 w-4 inline" /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}


function LotBuysPanel() {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/lot-buys"); setRows(data); }
    catch (e) { setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dissolve = async (lot) => {
    if (!window.confirm(`Dissolve "${lot.name}"? Its ${lot.items.length} items return to the catalog individually.`)) return;
    try { await api.delete(`/admin/lot-buys/${lot.id}`); toast.success("Lot Buy dissolved"); load(); }
    catch (e) { toast.error("Could not dissolve"); }
  };

  if (rows === null) return <div className="font-mono text-subtle text-sm">Loading lot buys…</div>;

  return (
    <div data-testid="lotbuys-panel">
      <div className="flex items-center gap-2 font-heading font-black text-lg tracking-tighter mb-2">
        <Layers className="h-5 w-5" /> {rows.length} LOT BUY{rows.length === 1 ? "" : "S"}
      </div>
      <p className="font-mono text-xs text-subtle mb-4 leading-relaxed">
        Create lot buys from the Products tab: filter to <strong>Held</strong>, select 2+ items showing the red <strong>20% Floor</strong> badge, then click <strong>Group into Lot Buy</strong>. Each lot sells the whole bundle at 20% off the combined floor price.
      </p>

      {rows.length === 0 ? (
        <div className="border border-dashed border-subtle p-10 text-center font-mono text-sm text-subtle" data-testid="lotbuys-empty">
          No lot buys yet.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((lot) => (
            <div key={lot.id} data-testid={`admin-lotbuy-${lot.id}`} className="border-2 border-ink bg-paper p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-heading font-black text-lg tracking-tighter">{lot.name}
                    {lot.quantity > 0
                      ? <span className="ml-2 bg-success text-ink px-2 py-0.5 text-[10px] uppercase tracking-wider border border-ink">Available</span>
                      : <span className="ml-2 bg-surface2 text-ink px-2 py-0.5 text-[10px] uppercase tracking-wider border border-ink">Sold</span>}
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-subtle">{lot.sku} · {lot.items.length} items</div>
                </div>
                <div className="text-right font-mono text-xs">
                  <div className="text-subtle line-through">{money(lot.combined_total)}</div>
                  <div className="font-heading font-black text-xl tracking-tighter">{money(lot.lot_price)}</div>
                </div>
              </div>
              <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 font-mono text-xs text-subtle">
                {lot.items.map((it) => (
                  <li key={it.id} className="flex justify-between gap-2"><span className="truncate">{it.name}</span><span>×{it.quantity} · {money(it.our_price)}</span></li>
                ))}
              </ul>
              <div className="mt-3 flex justify-end">
                <button data-testid={`dissolve-lotbuy-${lot.id}`} onClick={() => dissolve(lot)} className="border border-danger text-danger px-4 h-8 font-mono text-[10px] uppercase tracking-wider hover:bg-danger hover:text-white transition-colors">Dissolve Lot</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomersPanel() {  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [profileRefresh, setProfileRefresh] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/customers");
      setRows(data);
    } catch (e) { toast.error("Could not load customers"); setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/customers/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "str-surplus-customers.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Customer list downloaded");
    } catch (e) { toast.error("Export failed"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Remove ${c.name || c.email} from the customer list?`)) return;
    try { await api.delete(`/admin/customers/${c.id}`); toast.success("Customer removed"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  if (rows === null) return <div className="font-mono text-subtle text-sm">Loading customers…</div>;

  if (viewingId) {
    return (
      <>
        <CustomerProfile id={viewingId} refresh={profileRefresh} onBack={() => setViewingId(null)} onEdit={(c) => setEditing(c)} />
        {editing && (
          <CustomerDialog customer={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setProfileRefresh((x) => x + 1); load(); }} />
        )}
      </>
    );
  }

  return (
    <div data-testid="customers-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 font-heading font-black text-lg tracking-tighter">
          <Users className="h-5 w-5" /> {rows.length} CUSTOMER{rows.length === 1 ? "" : "S"}
        </div>
        <button data-testid="export-customers-button" onClick={exportCsv} className="flex items-center gap-2 border border-ink px-4 h-10 font-mono text-xs uppercase tracking-wider hover:bg-ink hover:text-paper transition-colors">
          <FileDown className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-subtle p-12 text-center font-mono text-sm text-subtle" data-testid="customers-empty">
          No customers yet. They appear here after their first order or account signup.
        </div>
      ) : (
        <div className="border border-ink overflow-x-auto">
          <table className="w-full font-mono text-xs">
            <thead className="bg-ink text-paper uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Customer</th>
                <th className="text-left p-3">Contact</th>
                <th className="text-left p-3">Location</th>
                <th className="text-right p-3">Orders</th>
                <th className="text-right p-3">Total Spent</th>
                <th className="text-left p-3">Last Order</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-t border-surface2 hover:bg-surface align-top">
                  <td className="p-3">
                    <button data-testid={`view-customer-${c.id}`} onClick={() => setViewingId(c.id)} className="font-bold text-left hover:text-link transition-colors">{c.name || c.email}</button>
                    {c.registered && <span className="ml-2 bg-success text-ink px-1.5 py-0.5 text-[9px] uppercase tracking-wider align-middle">Account</span>}
                    {c.company && <div className="text-subtle">{c.company}</div>}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1"><Mail className="h-3 w-3 text-subtle" />{c.email}</div>
                    {c.phone && <div className="text-subtle mt-1">{c.phone}</div>}
                  </td>
                  <td className="p-3 text-subtle">
                    {[c.city, c.state].filter(Boolean).join(", ") || "—"}
                    {c.county && <div className="text-[10px]">{c.county} County</div>}
                  </td>
                  <td className="p-3 text-right">{c.orders_count}</td>
                  <td className="p-3 text-right font-bold">{money(c.total_spent)}</td>
                  <td className="p-3">{(c.last_order_at || "").slice(0, 10) || "—"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button data-testid={`open-customer-${c.id}`} onClick={() => setViewingId(c.id)} className="p-1 hover:text-link" title="View profile"><Eye className="h-4 w-4 inline" /></button>
                    <button data-testid={`edit-customer-${c.id}`} onClick={() => setEditing(c)} className="p-1 hover:text-link ml-2"><Pencil className="h-4 w-4 inline" /></button>
                    <button data-testid={`delete-customer-${c.id}`} onClick={() => del(c)} className="p-1 hover:text-danger ml-2"><Trash2 className="h-4 w-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CustomerDialog customer={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function CustomerProfile({ id, refresh, onBack, onEdit }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/customers/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id, refresh]);

  if (data === null) return <div className="font-mono text-subtle text-sm">Loading profile…</div>;
  if (data === false) return <div className="font-mono text-danger text-sm">Could not load customer. <button onClick={onBack} className="underline">Back</button></div>;

  const { customer: c, orders, stats } = data;
  const loc = [c.address, c.city, c.state, c.zip].filter(Boolean).join(", ");

  return (
    <div data-testid="customer-profile">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="profile-back" onClick={onBack} className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider hover:text-link transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Customers
        </button>
        <button data-testid="profile-edit" onClick={() => onEdit(c)} className="flex items-center gap-2 border border-ink px-4 h-9 font-mono text-xs uppercase tracking-wider hover:bg-ink hover:text-paper transition-colors">
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      </div>

      {/* IDENTITY */}
      <div className="border-2 border-ink bg-surface p-5 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-heading font-black text-2xl tracking-tighter">{c.name || c.email}</h2>
          {c.user_id && <span className="bg-success text-ink px-2 py-0.5 text-[10px] uppercase tracking-wider border border-ink">Registered Account</span>}
        </div>
        {c.company && <div className="font-mono text-sm text-subtle mt-1">{c.company}</div>}
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 mt-4 font-mono text-xs">
          <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-subtle" /> {c.email}</div>
          {c.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-subtle" /> {c.phone}</div>}
          {loc && <div className="flex items-center gap-2 sm:col-span-2"><MapPin className="h-3.5 w-3.5 text-subtle" /> {loc}{c.county ? ` · ${c.county} County` : ""}</div>}
          {c.created_at && <div className="flex items-center gap-2 text-subtle"><Clock className="h-3.5 w-3.5" /> Since {(c.created_at || "").slice(0, 10)}</div>}
        </div>
        {c.notes && <div className="mt-4 bg-discount/40 border border-ink p-3 font-mono text-xs"><strong className="uppercase tracking-wider text-[10px]">Notes:</strong> {c.notes}</div>}
      </div>

      {/* LIFETIME STATS */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <MiniStat testid="ltv" icon={DollarSign} label="Lifetime Value" value={money(stats.lifetime_value)} accent />
        <MiniStat testid="orders" icon={ShoppingBag} label="Orders" value={stats.orders_count} />
        <MiniStat testid="aov" icon={DollarSign} label="Avg Order" value={money(stats.avg_order_value)} />
        <MiniStat testid="items" icon={Boxes} label="Items Bought" value={stats.items_purchased} />
        <MiniStat testid="last" icon={Clock} label="Last Order" value={(stats.last_order_at || "").slice(0, 10) || "—"} />
      </div>

      {/* ORDER HISTORY */}
      <div className="font-heading font-black text-lg tracking-tighter mb-3">ORDER HISTORY</div>
      {orders.length === 0 ? (
        <div className="border border-dashed border-subtle p-8 text-center font-mono text-sm text-subtle" data-testid="profile-orders-empty">
          No orders yet for this customer.
        </div>
      ) : (
        <div className="border border-ink overflow-x-auto">
          <table className="w-full font-mono text-xs">
            <thead className="bg-ink text-paper uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Order</th>
                <th className="text-left p-3">Items</th>
                <th className="text-left p-3">Fulfillment</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`profile-order-${o.id}`} className="border-t border-surface2 align-top">
                  <td className="p-3 whitespace-nowrap">#{String(o.id).slice(0, 8)}<div className="text-subtle">{(o.created_at || "").slice(0, 10)}</div></td>
                  <td className="p-3">{(o.items || []).map((it, i) => <div key={i} className="truncate max-w-xs">{it.name} <span className="text-subtle">×{it.quantity}</span></div>)}</td>
                  <td className="p-3">
                    {o.fulfillment === "pickup" ? <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Pickup</span>
                      : o.tracking_number ? <span className="flex items-center gap-1 text-link"><Truck className="h-3 w-3" /> {o.tracking_number}</span>
                        : <span className="flex items-center gap-1 text-subtle"><Truck className="h-3 w-3" /> Ship</span>}
                  </td>
                  <td className="p-3"><span className={`px-2 py-0.5 uppercase tracking-wider text-[10px] ${o.status === "paid" ? "bg-success text-ink" : o.status === "shipped" ? "bg-link text-white" : "bg-surface2"}`}>{o.status}</span></td>
                  <td className="p-3 text-right font-bold">{money(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={`stat-${testid}`} className={`border border-ink p-3 ${accent ? "bg-discount" : "bg-paper"}`}>
      <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-subtle"><Icon className="h-3 w-3" /> {label}</div>
      <div className="font-heading font-black text-xl tracking-tighter mt-1">{value}</div>
    </div>
  );
}

function CustomerDialog({ customer, onClose, onSaved }) {
  const [form, setForm] = useState(customer);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    const payload = {
      name: form.name || "", company: form.company || "", phone: form.phone || "",
      address: form.address || "", city: form.city || "", state: form.state || "",
      zip: form.zip || "", county: form.county || "", notes: form.notes || "",
    };
    try {
      await api.put(`/admin/customers/${form.id}`, payload);
      toast.success("Customer saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none border-2 border-ink">
        <DialogHeader><DialogTitle className="font-heading font-black text-2xl tracking-tighter">EDIT CUSTOMER</DialogTitle></DialogHeader>
        <div className="space-y-4 font-mono">
          <div className="bg-surface border border-ink p-3 text-xs flex items-center gap-2">
            <Mail className="h-4 w-4" /> {form.email} <span className="text-subtle">(email is the customer key and can't be changed)</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <In label="Name" testid="cf-name" value={form.name || ""} onChange={(v) => set("name", v)} />
            <In label="Company" testid="cf-company" value={form.company || ""} onChange={(v) => set("company", v)} />
            <In label="Phone" testid="cf-phone" value={form.phone || ""} onChange={(v) => set("phone", v)} />
            <In label="Address" testid="cf-address" value={form.address || ""} onChange={(v) => set("address", v)} />
            <In label="City" testid="cf-city" value={form.city || ""} onChange={(v) => set("city", v)} />
            <In label="State" testid="cf-state" value={form.state || ""} onChange={(v) => set("state", v)} />
            <In label="ZIP" testid="cf-zip" value={form.zip || ""} onChange={(v) => set("zip", v)} />
            <In label="County" testid="cf-county" value={form.county || ""} onChange={(v) => set("county", v)} />
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-subtle">Internal Notes</span>
            <textarea data-testid="cf-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="e.g. Repeat buyer, prefers local pickup, wholesale account…" className="mt-1 w-full border border-ink p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-6 h-11 border border-ink font-mono text-xs uppercase tracking-wider hover:bg-surface">Cancel</button>
            <button data-testid="cf-save" onClick={save} disabled={busy} className="px-8 h-11 bg-ink text-paper font-mono text-xs uppercase tracking-wider shadow-hardsm hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none transition-transform disabled:opacity-50">{busy ? "Saving…" : "Save Customer"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChannelsPanel() {
  const [status, setStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const [feedKey, setFeedKey] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [showAllIssues, setShowAllIssues] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const loadReadiness = useCallback(async () => {
    setReadinessLoading(true);
    try { const { data } = await api.get("/admin/channels/feed-readiness"); setReadiness(data); }
    catch (e) { toast.error("Feed readiness check failed"); }
    finally { setReadinessLoading(false); }
  }, []);

  useEffect(() => {
    api.get("/admin/channels/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/admin/channels/feed-key").then((r) => setFeedKey(r.data.key)).catch(() => {});
    loadReadiness();
  }, [loadReadiness]);

  const fbUrl = feedKey ? `${BACKEND}/api/feeds/facebook.csv?key=${feedKey}` : "";
  const wnUrl = feedKey ? `${BACKEND}/api/feeds/whatnot.csv?key=${feedKey}` : "";

  const download = async (which) => {
    try {
      const res = await api.get(`/admin/channels/${which}.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = which === "facebook" ? "facebook-marketplace-feed.csv" : "whatnot-inventory.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Feed downloaded");
    } catch (e) { toast.error("Download failed"); }
  };

  const copyText = (text) => {
    try { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); } catch (e) { /* noop */ }
  };

  const rotateKey = async () => {
    if (!window.confirm("Rotate the feed key? Your old feed URLs will stop working and you'll need to update them in Meta/Whatnot.")) return;
    try { const { data } = await api.post("/admin/channels/feed-key/rotate"); setFeedKey(data.key); toast.success("Feed key rotated"); }
    catch (e) { toast.error("Could not rotate key"); }
  };

  const copyHook = () => {
    try { navigator.clipboard.writeText(status.webhook_url); } catch (e) { /* noop */ }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  if (!status) return <div className="font-mono text-subtle text-sm">Loading channels…</div>;

  return (
    <div className="space-y-6" data-testid="channels-panel">
      <div className="border-2 border-ink bg-surface p-5">
        <div className="flex items-center gap-2 font-heading font-black text-lg tracking-tighter">
          <Radio className="h-5 w-5" /> CROSS-CHANNEL INVENTORY SYNC
        </div>
        <p className="font-mono text-xs text-subtle mt-2 leading-relaxed">
          Push your {status.in_stock_count} in-stock items to other marketplaces. Use the <strong>auto-refresh feed URLs</strong> below
          (recommended) so Meta &amp; Whatnot pull your live inventory on a schedule — no manual re-uploads. Or download a one-time CSV.
        </p>
      </div>

      <FeedReadinessCard readiness={readiness} loading={readinessLoading} onRefresh={loadReadiness} showAll={showAllIssues} setShowAll={setShowAllIssues} />

      <div className="border-2 border-ink bg-paper p-5" data-testid="auto-feeds">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 font-heading font-black text-lg tracking-tighter"><RefreshCw className="h-5 w-5" /> AUTO-REFRESH FEED URLS</div>
          <button data-testid="rotate-feed-key" onClick={rotateKey} className="font-mono text-[10px] uppercase tracking-wider border border-ink px-3 h-8 hover:bg-ink hover:text-paper transition-colors">Rotate Key</button>
        </div>
        <p className="font-mono text-xs text-subtle mt-2 leading-relaxed">
          These URLs always return your current live inventory (in-stock only, with prices &amp; markdowns applied). Keep them private — anyone with the URL can read your feed.
        </p>

        <FeedUrlRow label="Facebook / Meta Catalog feed" url={fbUrl} onCopy={() => copyText(fbUrl)} testid="fb-feed-url" />
        <FeedUrlRow label="Whatnot inventory feed" url={wnUrl} onCopy={() => copyText(wnUrl)} testid="wn-feed-url" />

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="border border-ink/30 p-3 font-mono text-[11px] leading-relaxed">
            <div className="uppercase tracking-widest font-bold mb-1">Facebook setup</div>
            <ol className="list-decimal ml-4 space-y-0.5 text-subtle">
              <li>Go to <b>Meta Commerce Manager</b> → your Catalog</li>
              <li><b>Data Sources → Add Items → Scheduled Feed</b></li>
              <li>Paste the Facebook feed URL above</li>
              <li>Set schedule (Daily or Hourly) → Save</li>
              <li>Meta re-pulls automatically; Marketplace visibility depends on your account eligibility</li>
            </ol>
          </div>
          <div className="border border-ink/30 p-3 font-mono text-[11px] leading-relaxed">
            <div className="uppercase tracking-widest font-bold mb-1">Whatnot setup</div>
            <ol className="list-decimal ml-4 space-y-0.5 text-subtle">
              <li>In <b>Whatnot Seller Hub</b>, open bulk / CSV inventory import</li>
              <li>Use the Whatnot feed URL above as your hosted CSV source (or download &amp; upload it)</li>
              <li>Refresh on your schedule to keep quantities current</li>
              <li>For live sold-sync, add the webhook below once you have Seller API access</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ChannelCard
          testid="channel-facebook" name="Facebook Marketplace"
          connected={status.facebook.connected} requirement={status.facebook.requirement}
          onDownload={() => download("facebook")} downloadLabel="Download one-time CSV"
        />
        <ChannelCard
          testid="channel-whatnot" name="Whatnot"
          connected={status.whatnot.connected} requirement={status.whatnot.requirement}
          onDownload={() => download("whatnot")} downloadLabel="Download one-time CSV"
        />
      </div>

      <div className="border border-ink p-5">
        <div className="font-mono text-[11px] uppercase tracking-widest text-subtle mb-2">Whatnot Sold-Sync Webhook</div>
        <p className="font-mono text-xs text-subtle mb-3 leading-relaxed">
          Once you have Whatnot Seller API access, paste this endpoint into Whatnot and set the shared secret in the
          app environment. Items sold on Whatnot will then decrement your stock here automatically.
        </p>
        <div className="flex items-center gap-2">
          <code data-testid="whatnot-webhook-url" className="flex-1 truncate bg-surface border border-ink px-3 h-10 leading-10 font-mono text-xs">{status.webhook_url}</code>
          <button data-testid="copy-webhook" onClick={copyHook} className="flex items-center gap-2 border border-ink px-4 h-10 font-mono text-xs uppercase tracking-wider hover:bg-ink hover:text-paper transition-colors">
            <Copy className="h-4 w-4" /> {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FeedReadinessCard({ readiness, loading, onRefresh, showAll, setShowAll }) {
  if (loading && !readiness) return (
    <div className="border-2 border-ink bg-paper p-5 font-mono text-sm text-subtle">Checking feed readiness…</div>
  );
  if (!readiness) return null;
  const { total_skus, ready_skus, issue_count, counts, issues } = readiness;
  const okColor = issue_count === 0 ? "text-success" : issue_count <= 5 ? "text-danger" : "text-danger";
  const displayed = showAll ? issues : issues.slice(0, 8);
  return (
    <div className="border-2 border-ink bg-paper p-5" data-testid="feed-readiness-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 font-heading font-black text-lg tracking-tighter">
          <Check className="h-5 w-5" /> FEED READINESS CHECK
        </div>
        <button data-testid="refresh-readiness" onClick={onRefresh} disabled={loading} className="font-mono text-[10px] uppercase tracking-wider border border-ink px-3 h-8 hover:bg-ink hover:text-paper transition-colors disabled:opacity-40">
          {loading ? "Checking…" : "Re-scan"}
        </button>
      </div>
      <p className="font-mono text-xs text-subtle mt-2 leading-relaxed">
        Spot items that Facebook or Whatnot will reject or downrank before you push. Fix the missing fields in the Products tab, then re-scan.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
        <ReadinessStat label="Total SKUs" value={total_skus} />
        <ReadinessStat label="Ready to Push" value={ready_skus} tone="ok" />
        <ReadinessStat label="Have Issues" value={issue_count} tone={issue_count === 0 ? "ok" : "bad"} testid="readiness-issue-count" />
      </div>

      {issue_count > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
          <MissingChip label="No Image" count={counts.missing_image} />
          <MissingChip label="No Description" count={counts.missing_description} />
          <MissingChip label="No Brand" count={counts.missing_brand} />
          <MissingChip label="No Category" count={counts.missing_category} />
        </div>
      )}

      {issues.length > 0 ? (
        <div className="mt-4 border border-ink/30" data-testid="readiness-issue-list">
          <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[80px_1fr_120px_auto] gap-2 px-3 py-2 bg-surface font-mono text-[10px] uppercase tracking-widest text-subtle border-b border-ink/30">
            <span className="hidden sm:inline">SKU</span>
            <span>Product</span>
            <span className="hidden sm:inline">Category</span>
            <span className="text-right">Missing</span>
          </div>
          {displayed.map((it) => (
            <div key={it.id} className="grid grid-cols-[1fr_auto] sm:grid-cols-[80px_1fr_120px_auto] gap-2 px-3 py-2 border-b border-ink/10 items-center">
              <span className="hidden sm:inline font-mono text-[11px] text-subtle">{it.sku || "—"}</span>
              <span className="font-mono text-xs truncate" title={it.name}>{it.name}</span>
              <span className="hidden sm:inline font-mono text-[11px] text-subtle truncate">{it.category || "—"}</span>
              <span className="flex flex-wrap gap-1 justify-end">
                {it.missing.map((f) => (
                  <span key={f} className={`font-mono text-[9px] uppercase tracking-wider border px-1.5 py-0.5 ${f === "image" ? "border-danger text-danger" : "border-ink text-ink"}`}>{f}</span>
                ))}
              </span>
            </div>
          ))}
          {issues.length > 8 && (
            <button onClick={() => setShowAll(!showAll)} className="w-full text-center font-mono text-[11px] uppercase tracking-wider py-2 hover:bg-surface transition-colors">
              {showAll ? "Show less" : `Show all ${issues.length} issues`}
            </button>
          )}
        </div>
      ) : (
        <div className="mt-4 border-2 border-success bg-success/10 p-3 font-mono text-sm text-center">
          ✓ All {total_skus} in-stock SKUs are feed-ready. You're clear to push to Facebook &amp; Whatnot.
        </div>
      )}
    </div>
  );
}

function ReadinessStat({ label, value, tone = "", testid }) {
  const color = tone === "ok" ? "text-success" : tone === "bad" ? "text-danger" : "";
  return (
    <div className="border border-ink p-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-subtle">{label}</div>
      <div data-testid={testid} className={`font-heading font-black text-2xl tracking-tighter ${color}`}>{value}</div>
    </div>
  );
}

function MissingChip({ label, count }) {
  const tone = count > 0 ? "border-danger bg-danger/10 text-danger" : "border-ink/20 text-subtle";
  return (
    <div className={`border px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider flex justify-between ${tone}`}>
      <span>{label}</span><span className="font-bold">{count}</span>
    </div>
  );
}

function FeedUrlRow({ label, url, onCopy, testid }) {
  return (
    <div className="mt-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-subtle mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code data-testid={testid} className="flex-1 truncate bg-surface border border-ink px-3 h-10 leading-10 font-mono text-xs">{url || "…"}</code>
        <button data-testid={`${testid}-copy`} onClick={onCopy} disabled={!url} className="flex items-center gap-2 border border-ink px-4 h-10 font-mono text-xs uppercase tracking-wider hover:bg-ink hover:text-paper transition-colors disabled:opacity-40">
          <Copy className="h-4 w-4" /> Copy
        </button>
      </div>
    </div>
  );
}

function ChannelCard({ testid, name, connected, requirement, onDownload, downloadLabel }) {
  return (
    <div data-testid={testid} className="border-2 border-ink bg-paper p-5 flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading font-black text-lg tracking-tighter"><Store className="h-5 w-5" /> {name}</div>
        <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-ink ${connected ? "bg-success text-ink" : "bg-surface2 text-ink"}`}>
          {connected ? "Connected" : "Not Connected"}
        </span>
      </div>
      <p className="font-mono text-xs text-subtle mt-3 leading-relaxed flex-1">{requirement}</p>
      <button data-testid={`${testid}-download`} onClick={onDownload} className="mt-4 flex items-center justify-center gap-2 bg-ink text-paper h-10 font-mono text-xs uppercase tracking-wider hover:bg-link transition-colors">
        <FileDown className="h-4 w-4" /> {downloadLabel}
      </button>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {  return (
    <div className={`border border-ink p-4 ${accent ? "bg-discount" : "bg-paper"}`}>
      <Icon className="h-5 w-5 mb-2" />
      <div className="font-heading font-black text-2xl tracking-tighter leading-none">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-subtle mt-1">{label}</div>
    </div>
  );
}

function ManifestUpload({ onDone }) {
  const [file, setFile] = useState(null);
  const [lot, setLot] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) { toast.error("Choose a manifest file"); return; }
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("lot_name", lot);
    try {
      const { data } = await api.post("/admin/manifest", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.created} products imported — held for review`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="border-2 border-ink p-6">
        <div className="flex items-center gap-2 font-heading font-black text-xl tracking-tighter mb-4"><FileSpreadsheet className="h-5 w-5" /> IMPORT MANIFEST</div>
        <p className="font-mono text-xs text-subtle mb-4 leading-relaxed">Upload a CSV, Excel (.xlsx), or PDF shipping manifest. We auto-detect columns for name, SKU, list price, quantity, condition, weight & category and auto-populate product images by name.</p>

        <label className="block border-2 border-dashed border-ink p-6 text-center cursor-pointer hover:bg-surface transition-colors">
          <Upload className="h-6 w-6 mx-auto mb-2" />
          <span className="font-mono text-xs">{file ? file.name : "Click to choose file (.csv, .xlsx, .pdf)"}</span>
          <input data-testid="manifest-file" type="file" accept=".csv,.xlsx,.xls,.pdf" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
        </label>

        <div className="mt-4">
          <label className="block">
            <span className="font-mono text-[11px] uppercase tracking-wider text-subtle">Lot Name</span>
            <input data-testid="manifest-lot" value={lot} onChange={(e) => setLot(e.target.value)} placeholder="e.g. Lot #A-204" className="mt-1 w-full border border-ink h-10 px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
          </label>
        </div>
        <div className="font-mono text-[10px] text-subtle mt-2 leading-relaxed border border-ink/20 p-2">
          <div className="uppercase tracking-wider font-bold text-ink mb-1">Auto-pricing by condition (× Unit Retail)</div>
          NIB 70% · Like New 65% · Scratch &amp; Dent 60% · Floor Model 60% · Good/Fair 50% · Used 55% · As-Is 20%
        </div>

        <button data-testid="manifest-upload-button" onClick={upload} disabled={busy} className="mt-4 w-full bg-ink text-paper h-12 font-mono text-sm uppercase tracking-[0.15em] shadow-hard hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-transform disabled:opacity-50">
          {busy ? "Importing…" : "Import Manifest"}
        </button>
      </div>

      <div className="border border-ink p-6 bg-surface">
        <div className="font-mono text-[11px] uppercase tracking-widest text-subtle mb-3">Sample CSV Format</div>
        <pre className="font-mono text-[11px] bg-paper border border-ink p-3 overflow-x-auto">{`SKU,Name,Condition,Unit Retail,Qty,Weight,Category,Image URL
A100,Ring Video Doorbell,New / Brand New / In Box (NIB),99.99,10,1.2,Smart Home,https://drive.google.com/file/d/FILEID/view?usp=sharing
A101,Nespresso Machine,Used / Pre-owned / Tested & Working,199.00,5,8.5,Kitchen,
A102,Bath Towel Set,Like New / Mint Condition,49.99,25,4,Linens,https://example.com/towels.jpg`}</pre>
        <p className="font-mono text-[10px] text-subtle mt-2 leading-relaxed">
          <b>Image URL</b> column is optional. Paste a direct link or a Google Drive share link (set the file/folder to "Anyone with the link"). We download &amp; re-host each image automatically. Multiple images? Separate with commas. Leave blank to auto-fetch by UPC.
        </p>
        {result && (
          <div className="mt-4 bg-discount text-ink border-2 border-ink p-4 font-mono text-xs" data-testid="manifest-result">
            ✓ Imported {result.created} products{result.lot_name ? ` into "${result.lot_name}"` : ""} — <strong>held for review</strong>. Go to the Products tab and click <strong>Publish</strong> to make them live on the store.
          </div>
        )}
      </div>
    </div>
  );
}

function ProductDialog({ open, onClose, product, conditions, onSaved }) {
  const [form, setForm] = useState(product);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [vidUploading, setVidUploading] = useState(false);
  const [vidProgress, setVidProgress] = useState(0);
  const [fetchingUpc, setFetchingUpc] = useState(false);
  const [fetchingSpecs, setFetchingSpecs] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const moveImage = (from, to) => {
    if (from == null || to == null || from === to) return;
    const imgs = [...(form.images || [])];
    const [m] = imgs.splice(from, 1);
    imgs.splice(to, 0, m);
    set("images", imgs);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const autofillSpecs = async () => {
    if (!form.name && !form.upc) { toast.error("Enter a product name or UPC first"); return; }
    setFetchingSpecs(true);
    const t = toast.loading("Researching product specs…");
    try {
      const { data } = await api.post("/admin/autofill-specs", { name: form.name, brand: form.brand, upc: form.upc });
      set("details", data.details);
      set("specs_status", "ai_draft");
      toast.success("Specs filled in — review & edit, then Verify before it goes live", { id: t });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not fetch specs", { id: t }); } finally { setFetchingSpecs(false); }
  };


  const fetchUpc = async () => {
    if (!form.upc) { toast.error("Enter a UPC first"); return; }
    setFetchingUpc(true);
    try {
      const { data } = await api.post("/admin/upc-image", { upc: form.upc });
      set("images", [data.url, ...(form.images || []).filter((i) => i !== data.url)]);
      toast.success(data.title ? `Found: ${data.title.slice(0, 48)}` : "Product image found");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setFetchingUpc(false); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("images", [...(form.images || []), data.url]);
      toast.success("Photo uploaded");
    } catch (err) { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const uploadVideo = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setVidUploading(true); setVidProgress(0);
    try {
      const initFd = new FormData();
      initFd.append("filename", f.name);
      const { data: init } = await api.post("/admin/upload-media/init", initFd, { headers: { "Content-Type": "multipart/form-data" } });
      const CHUNK = 5 * 1024 * 1024;
      let offset = 0;
      while (offset < f.size) {
        const slice = f.slice(offset, offset + CHUNK);
        const cfd = new FormData();
        cfd.append("upload_id", init.upload_id);
        cfd.append("chunk", slice, "chunk");
        await api.post("/admin/upload-media/chunk", cfd, { headers: { "Content-Type": "multipart/form-data" } });
        offset += CHUNK;
        setVidProgress(Math.min(99, Math.round((offset / f.size) * 100)));
      }
      const compFd = new FormData();
      compFd.append("upload_id", init.upload_id);
      compFd.append("ext", init.ext);
      compFd.append("content_type", f.type || "video/mp4");
      const { data } = await api.post("/admin/upload-media/complete", compFd, { headers: { "Content-Type": "multipart/form-data" } });
      set("videos", [...(form.videos || []), data.url]);
      setVidProgress(100);
      toast.success("Video uploaded");
    } catch (err) { toast.error("Video upload failed"); } finally { setVidUploading(false); }
  };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setBusy(true);
    const payload = {
      ...form,
      list_price: Number(form.list_price) || 0,
      our_price: Number(form.our_price) || 0,
      cost: Number(form.cost) || 0,
      quantity: parseInt(form.quantity) || 0,
      weight_lbs: Number(form.weight_lbs) || 1,
    };
    try {
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none border-2 border-ink">
        <DialogHeader><DialogTitle className="font-heading font-black text-2xl tracking-tighter">{form.id ? "EDIT PRODUCT" : "NEW PRODUCT"}</DialogTitle></DialogHeader>
        <div className="space-y-4 font-mono">
          <div className="grid grid-cols-2 gap-3">
            <In label="Name *" testid="pf-name" value={form.name} onChange={(v) => set("name", v)} span />
            <In label="SKU" testid="pf-sku" value={form.sku} onChange={(v) => set("sku", v)} />
            <In label="Inv #" testid="pf-inv" value={form.inv_number} onChange={(v) => set("inv_number", v)} />
            <In label="UPC" testid="pf-upc" value={form.upc} onChange={(v) => set("upc", v)} />
            <In label="Brand" testid="pf-brand" value={form.brand} onChange={(v) => set("brand", v)} />
            <In label="Category" testid="pf-category" value={form.category} onChange={(v) => set("category", v)} />
            <In label="Lot Name" testid="pf-lot" value={form.lot_name} onChange={(v) => set("lot_name", v)} />
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-subtle">Condition</span>
              <select data-testid="pf-condition" value={form.condition} onChange={(e) => set("condition", e.target.value)} className="mt-1 w-full border border-ink h-10 px-2 text-sm bg-paper focus:outline-none">
                {conditions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <In label="List Price" testid="pf-list" type="number" value={form.list_price} onChange={(v) => set("list_price", v)} />
            <In label="Our Price" testid="pf-our" type="number" value={form.our_price} onChange={(v) => set("our_price", v)} />
            <In label="Cost / Unit" testid="pf-cost" type="number" value={form.cost} onChange={(v) => set("cost", v)} />
            <In label="Quantity" testid="pf-qty" type="number" value={form.quantity} onChange={(v) => set("quantity", v)} />
            <In label="Weight (lbs)" testid="pf-weight" type="number" value={form.weight_lbs} onChange={(v) => set("weight_lbs", v)} />
          </div>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-subtle">Description</span>
            <textarea data-testid="pf-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="mt-1 w-full border border-ink p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-subtle">Details / Specifications (visible to buyers)</span>
            <div className="flex items-center justify-between mt-1 mb-1 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-subtle font-mono">Auto-research from the manufacturer/retail listing</span>
                {form.specs_status === "ai_draft" && (
                  <span data-testid="pf-specs-draft-flag" className="inline-flex items-center gap-1 bg-discount text-ink border border-ink px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono">AI-Drafted · Verify</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {form.specs_status === "ai_draft" && (
                  <button type="button" data-testid="pf-verify-specs" onClick={() => { set("specs_status", "approved"); toast.success("Marked verified — will go live & appear in feeds on Save"); }} className="inline-flex items-center gap-1.5 border border-success text-success px-3 py-1.5 text-[11px] uppercase tracking-wider font-mono hover:bg-success hover:text-ink transition-colors">
                    <Check className="h-3.5 w-3.5" />Mark Verified
                  </button>
                )}
                <button
                  type="button"
                  data-testid="pf-autofill-specs"
                  onClick={autofillSpecs}
                  disabled={fetchingSpecs}
                  className="inline-flex items-center gap-1.5 border border-ink px-3 py-1.5 text-[11px] uppercase tracking-wider font-mono hover:bg-ink hover:text-paper transition-colors disabled:opacity-40"
                >
                  <Sparkles className={`h-3.5 w-3.5 ${fetchingSpecs ? "animate-pulse" : ""}`} />
                  {fetchingSpecs ? "Researching…" : "Auto-fill Specs"}
                </button>
              </div>
            </div>
            {form.specs_status === "ai_draft" && (
              <p className="text-[11px] text-danger font-mono mb-1">Not shown to buyers or exported to Facebook/Whatnot until you Mark Verified.</p>
            )}
            <textarea data-testid="pf-details" value={form.details || ""} onChange={(e) => set("details", e.target.value)} rows={6} placeholder={"One item per line — use blank lines or headers like 'Includes:' / 'Features:'.\n\nExample:\n18/8 Stainless Steel Mesh\nFDA Food Safe Silicone Rim & Handle\nDishwasher Safe\n\nIncludes:\n(1) 13\" Splatter Screen\n(1) 10.4\" Splatter Screen"} className="w-full border border-ink p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ink" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-subtle">Condition Notes (visible to buyers)</span>
            <textarea data-testid="pf-notes" value={form.condition_notes} onChange={(e) => set("condition_notes", e.target.value)} rows={2} placeholder="e.g. Powers on, missing remote, light scratches on lid." className="mt-1 w-full border border-ink p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
          </label>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wider text-subtle">Photos <span className="normal-case text-subtle/70">· drag to reorder · first = hero</span></span>
              <button type="button" data-testid="pf-fetch-upc" onClick={fetchUpc} disabled={fetchingUpc} className="flex items-center gap-1 border border-ink px-2 py-1 text-[10px] uppercase tracking-wider hover:bg-ink hover:text-paper transition-colors disabled:opacity-50">
                <ScanBarcode className="h-3.5 w-3.5" /> {fetchingUpc ? "Searching…" : "Fetch Real Photo From UPC"}
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(form.images || []).map((img, i) => (
                <div key={img + i} draggable
                  data-testid={`pf-image-${i}`}
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); moveImage(dragIdx, i); setDragIdx(null); }}
                  onDragEnd={() => setDragIdx(null)}
                  className={`relative h-20 w-20 border border-ink cursor-move ${i === 0 ? "ring-2 ring-ink ring-offset-1" : ""} ${dragIdx === i ? "opacity-40" : ""}`}>
                  <img src={assetUrl(img)} alt="" className="h-full w-full object-cover pointer-events-none" />
                  {i === 0 ? (
                    <span className="absolute bottom-0 inset-x-0 bg-ink text-paper text-[8px] text-center uppercase tracking-wider py-0.5">Hero</span>
                  ) : (
                    <button type="button" data-testid={`pf-make-hero-${i}`} onClick={() => moveImage(i, 0)} title="Make hero"
                      className="absolute bottom-0 inset-x-0 bg-paper/90 text-ink text-[8px] text-center uppercase tracking-wider py-0.5 opacity-0 hover:opacity-100 border-t border-ink">Set Hero</button>
                  )}
                  <button type="button" onClick={() => set("images", form.images.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-danger text-white h-5 w-5 flex items-center justify-center border border-ink"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label className="h-20 w-20 border-2 border-dashed border-ink flex flex-col items-center justify-center cursor-pointer hover:bg-surface text-subtle">
                <ImageIcon className="h-5 w-5" />
                <span className="text-[9px] mt-1">{uploading ? "…" : "ADD"}</span>
                <input data-testid="pf-upload" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
              </label>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wider text-subtle">Video (shows as hero — autoplays on cards)</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(form.videos || []).map((vid, i) => (
                <div key={i} className="relative h-20 w-20 border border-ink bg-ink">
                  <video src={assetUrl(vid)} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                  <button data-testid={`pf-video-remove-${i}`} onClick={() => set("videos", form.videos.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-danger text-white h-5 w-5 flex items-center justify-center border border-ink"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label className="h-20 w-20 border-2 border-dashed border-ink flex flex-col items-center justify-center cursor-pointer hover:bg-surface text-subtle">
                <Video className="h-5 w-5" />
                <span className="text-[9px] mt-1 text-center leading-tight">{vidUploading ? `${vidProgress}%` : "ADD"}</span>
                <input data-testid="pf-upload-video" type="file" accept="video/*" disabled={vidUploading} className="hidden" onChange={uploadVideo} />
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-6 h-11 border border-ink font-mono text-xs uppercase tracking-wider hover:bg-surface">Cancel</button>
            <button data-testid="pf-save" onClick={save} disabled={busy} className="px-8 h-11 bg-ink text-paper font-mono text-xs uppercase tracking-wider shadow-hardsm hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none transition-transform disabled:opacity-50">{busy ? "Saving…" : "Save Product"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function In({ label, testid, value, onChange, type = "text", span }) {
  return (
    <label className={`block ${span ? "col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-wider text-subtle">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full border border-ink h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
    </label>
  );
}


function EditableOurPrice({ p, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(p.our_price ?? ""));
  useEffect(() => { setVal(String(p.our_price ?? "")); }, [p.our_price]);
  const commit = async () => { const ok = await onSave(p.id, val); if (ok) setEditing(false); };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 justify-end">
        <input autoFocus data-testid={`admin-price-input-${p.id}`} type="number" step="0.01" value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setVal(String(p.our_price ?? "")); setEditing(false); } }}
          className="w-20 border border-ink h-7 px-1 text-xs text-right font-mono" />
        <button data-testid={`admin-price-save-${p.id}`} onClick={commit} className="h-6 w-6 flex items-center justify-center bg-ink text-paper"><Check className="h-3 w-3" /></button>
      </span>
    );
  }
  return (
    <button data-testid={`admin-price-edit-${p.id}`} onClick={() => setEditing(true)} className="inline-flex items-center gap-1 font-bold hover:text-link">
      {money(p.our_price)}<Pencil className="h-3 w-3 opacity-40" />
    </button>
  );
}

function MarginCell({ p, minMargin }) {
  const cost = p.cost || 0;
  if (!cost) return <span className="text-subtle">—</span>;
  const marginPer = (p.our_price || 0) - cost;
  const pct = p.our_price ? Math.round((marginPer / p.our_price) * 100) : 0;
  const below = minMargin != null && pct < minMargin;
  const color = marginPer < 0 ? "#FF3B30" : below ? "#B45309" : "#16A34A";
  return (
    <span data-testid={`margin-${p.id}`} style={{ color }} className="whitespace-nowrap inline-flex items-center gap-1 justify-end">
      {below && <span title={`Below ${minMargin}% floor`} data-testid={`margin-warn-${p.id}`} className="bg-discount text-ink text-[9px] px-1 py-0.5 tracking-wider">LOW</span>}
      {money(marginPer)} <span className="text-[10px]">({pct}%)</span>
    </span>
  );
}
